import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { SingleUploader } from '../../components/MediaUploader';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const EMPTY = { name: '', title: '', license: '', phone: '', email: '', photo: '', bio: '', listings: 0, sold: 0, rating: 5.0, languages: [] };

export default function AdminAgentForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(id);

  useEffect(() => {
    if (isEdit) api.get('/agents/' + id).then(r => {
      const a = r.data; delete a.active_listings;
      setForm({ ...EMPTY, ...a });
    });
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, listings: parseInt(form.listings) || 0, sold: parseInt(form.sold) || 0, rating: parseFloat(form.rating) || 5.0 };
      if (isEdit) await api.put('/admin/agents/' + id, payload);
      else await api.post('/admin/agents', payload);
      toast({ title: 'Saved' });
      navigate('/admin/agents');
    } catch (err) {
      toast({ title: 'Save failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl">
      <Link to="/admin/agents" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-[#DC2626] mb-4"><ArrowLeft className="w-4 h-4" /> Back</Link>
      <h1 className="text-2xl font-bold text-neutral-900 mb-6">{isEdit ? 'Edit Advisor' : 'New Advisor'}</h1>
      <form onSubmit={submit} className="space-y-5">
        <div className="bg-white p-6 rounded-xl border border-neutral-200 space-y-4">
          <SingleUploader value={form.photo} onChange={(v) => set('photo', v)} label="Profile Photo" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Full Name *</label><Input required value={form.name} onChange={(e) => set('name', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Title</label><Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Senior Broker" /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">PRC License</label><Input value={form.license} onChange={(e) => set('license', e.target.value)} placeholder="PRC #0012345" /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Phone</label><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+63 917 ..." /></div>
            <div className="md:col-span-2"><label className="text-sm font-medium text-neutral-700 mb-1 block">Email</label><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></div>
            <div className="md:col-span-2"><label className="text-sm font-medium text-neutral-700 mb-1 block">Bio</label><Textarea rows={4} value={form.bio} onChange={(e) => set('bio', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Listings</label><Input type="number" value={form.listings} onChange={(e) => set('listings', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Sold</label><Input type="number" value={form.sold} onChange={(e) => set('sold', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Rating (0-5)</label><Input type="number" step="0.1" value={form.rating} onChange={(e) => set('rating', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Languages (comma-separated)</label><Input value={(form.languages || []).join(', ')} onChange={(e) => set('languages', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="English, Tagalog, Cebuano" /></div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3">
          <Link to="/admin/agents"><Button type="button" variant="outline">Cancel</Button></Link>
          <Button type="submit" disabled={saving} className="btn-primary gap-2">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</Button>
        </div>
      </form>
    </div>
  );
}
